import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl",
  role: "dialog",
  "aria-modal": "true",
  "aria-labelledby": "upload-report-title"
}
const _hoisted_2 = { class: "flex items-start justify-between gap-4" }
const _hoisted_3 = {
  id: "upload-report-title",
  class: "text-xl font-black text-slate-900"
}
const _hoisted_4 = { class: "mt-1 text-sm text-slate-500" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = { class: "mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = {
  key: 1,
  class: "mt-2 text-amber-700"
}
const _hoisted_10 = {
  for: "report-pdf",
  class: "mt-5 flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-slate-300 px-5 py-8 text-center hover:border-blue-400"
}
const _hoisted_11 = ["disabled"]
const _hoisted_12 = {
  key: 0,
  class: "mt-3 flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900"
}
const _hoisted_13 = { class: "truncate" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = {
  key: 1,
  class: "mt-2 text-sm font-semibold text-red-600",
  role: "alert"
}
const _hoisted_16 = {
  key: 2,
  class: "mt-4"
}
const _hoisted_17 = { class: "mb-1 flex justify-between text-xs font-bold text-slate-600" }
const _hoisted_18 = ["aria-valuenow"]
const _hoisted_19 = {
  key: 3,
  class: "mt-3 text-sm font-semibold text-red-600",
  role: "alert"
}
const _hoisted_20 = { class: "mt-6 flex justify-end gap-3" }
const _hoisted_21 = ["disabled"]
const _hoisted_22 = ["disabled"]

import { computed, ref, watch } from "vue";
import { FileUp } from "lucide-vue-next";
import type { ReportRecord } from "@/types/reports";
import { replaceReportPdf, uploadReportPdf } from "@/services/reportsService";


export default /*@__PURE__*/_defineComponent({
  __name: 'UploadReportModal',
  props: {
    open: { type: Boolean },
    mode: {},
    clientId: {},
    clientCode: {},
    clientName: {},
    monthNumber: {},
    monthLabel: {},
    dateFrom: {},
    dateTo: {},
    existingReport: {}
  },
  emits: ["update:open", "uploaded", "updated"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const file = ref<File | null>(null); const validationError = ref(""); const serverError = ref(""); const busy = ref(false); const progress = ref(0); const fileInput = ref<HTMLInputElement | null>(null);
const period = computed(() => [props.dateFrom, props.dateTo].filter(Boolean).join(" – "));
watch(() => props.open, (open) => { if (open) { file.value = null; validationError.value = ""; serverError.value = ""; progress.value = 0; } });
function close() { if (!busy.value) emit("update:open", false); }
function validate(candidate: File | null) { if (!candidate) return "Please choose a PDF file."; if (candidate.size <= 0) return "The selected file is empty."; if (candidate.size > 25 * 1024 * 1024) return "The PDF exceeds the maximum allowed file size of 25 MB."; if (candidate.type && candidate.type !== "application/pdf") return "Only PDF files are allowed."; if (!candidate.name.toLowerCase().endsWith(".pdf")) return "Only PDF files are allowed."; return ""; }
function selectFile(event: Event) { const candidate = (event.target as HTMLInputElement).files?.[0] || null; validationError.value = validate(candidate); file.value = validationError.value ? null : candidate; serverError.value = ""; }
function removeFile() { file.value = null; if (fileInput.value) fileInput.value.value = ""; }
function formatSize(bytes: number) { return `${(bytes / 1024 / 1024).toFixed(2)} MB`; }
function uploadErrorMessage(error: any) {
  const message = error?.response?.data?.message;
  if (message === "The report failed to upload.") {
    return "The PDF exceeds its configured upload limit. Please try a smaller PDF or contact an administrator.";
  }
  return message || "Unable to upload the PDF. Please try again.";
}
async function submit() { if (!file.value) return; busy.value = true; serverError.value = ""; progress.value = 0; const form = new FormData(); form.append("report", file.value); form.append("client_id", props.clientId); form.append("month_number", String(props.monthNumber)); form.append("report_month_label", props.monthLabel); if (props.dateFrom) form.append("date_from", props.dateFrom); if (props.dateTo) form.append("date_to", props.dateTo); try { const report = props.mode === "update" && props.existingReport ? await replaceReportPdf(props.existingReport.id, form, (value) => progress.value = value) : await uploadReportPdf(form, (value) => progress.value = value); emit(props.mode === "update" ? "updated" : "uploaded", report); emit("update:open", false); } catch (error: any) { serverError.value = uploadErrorMessage(error); } finally { busy.value = false; } }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (__props.open)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4",
          onClick: _withModifiers(close, ["self"])
        }, [
          _createElementVNode("section", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", null, [
                _createElementVNode("h2", _hoisted_3, _toDisplayString(__props.mode === "update" ? "Update Report" : "Upload Report") + " — " + _toDisplayString(__props.monthLabel), 1),
                _createElementVNode("p", _hoisted_4, [
                  _createTextVNode(_toDisplayString(__props.clientName), 1),
                  (__props.clientCode)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_5, " · " + _toDisplayString(__props.clientCode), 1))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "text-2xl text-slate-400",
                disabled: busy.value,
                "aria-label": "Close",
                onClick: close
              }, " × ", 8, _hoisted_6)
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("p", null, [
                _cache[0] || (_cache[0] = _createElementVNode("strong", null, "Month:", -1)),
                _createTextVNode(" " + _toDisplayString(__props.monthLabel), 1)
              ]),
              (period.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_8, [
                    _cache[1] || (_cache[1] = _createElementVNode("strong", null, "Reporting period:", -1)),
                    _createTextVNode(" " + _toDisplayString(period.value), 1)
                  ]))
                : _createCommentVNode("", true),
              (__props.mode === 'update')
                ? (_openBlock(), _createElementBlock("p", _hoisted_9, " Uploading this PDF will replace the current active report after the upload succeeds. "))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("label", _hoisted_10, [
              _createVNode(_unref(FileUp), {
                class: "h-8 w-8 text-blue-600",
                "aria-hidden": "true"
              }),
              _cache[2] || (_cache[2] = _createElementVNode("span", { class: "mt-2 font-bold text-slate-800" }, "Choose a PDF report", -1)),
              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "mt-1 text-xs text-slate-500" }, "PDF only, maximum 25 MB", -1)),
              _createElementVNode("input", {
                id: "report-pdf",
                ref_key: "fileInput",
                ref: fileInput,
                type: "file",
                accept: "application/pdf,.pdf",
                class: "sr-only",
                disabled: busy.value,
                onChange: selectFile
              }, null, 40, _hoisted_11)
            ]),
            (file.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _createElementVNode("span", _hoisted_13, _toDisplayString(file.value.name) + " · " + _toDisplayString(formatSize(file.value.size)), 1),
                  _createElementVNode("button", {
                    type: "button",
                    class: "ml-3 font-bold",
                    disabled: busy.value,
                    onClick: removeFile
                  }, " Remove ", 8, _hoisted_14)
                ]))
              : _createCommentVNode("", true),
            (validationError.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(validationError.value), 1))
              : _createCommentVNode("", true),
            (busy.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                  _createElementVNode("div", _hoisted_17, [
                    _cache[4] || (_cache[4] = _createElementVNode("span", null, "Uploading…", -1)),
                    _createElementVNode("span", null, _toDisplayString(progress.value) + "%", 1)
                  ]),
                  _createElementVNode("div", {
                    class: "h-2 overflow-hidden rounded-full bg-slate-200",
                    role: "progressbar",
                    "aria-valuenow": progress.value,
                    "aria-valuemin": "0",
                    "aria-valuemax": "100"
                  }, [
                    _createElementVNode("div", {
                      class: "h-full bg-blue-600 transition-all",
                      style: _normalizeStyle({ width: `${progress.value}%` })
                    }, null, 4)
                  ], 8, _hoisted_18)
                ]))
              : _createCommentVNode("", true),
            (serverError.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(serverError.value), 1))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_20, [
              _createElementVNode("button", {
                type: "button",
                class: "rounded-lg border border-slate-300 px-4 py-2 font-bold text-slate-700",
                disabled: busy.value,
                onClick: close
              }, " Cancel ", 8, _hoisted_21),
              _createElementVNode("button", {
                type: "button",
                class: "rounded-lg bg-[#173b5f] px-4 py-2 font-bold text-white disabled:cursor-not-allowed disabled:opacity-50",
                disabled: !file.value || !!validationError.value || busy.value,
                onClick: submit
              }, _toDisplayString(__props.mode === "update" ? "Update Report" : "Upload Report"), 9, _hoisted_22)
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})